import React, { useState } from "react";
import styled from "@emotion/styled";
import { Search as SearchIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { darken } from "polished";

import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  InputBase,
  CardContent,
  Card as MuiCard,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
  min-width: 200px;
`;

const Customer = styled.div`
  display: flex;
  align-items: center;
`;

function createData(
  customer,
  customerEmail,
  customerAvatar,
  status,
  amount,
  id,
  date
) {
  return { customer, customerEmail, customerAvatar, status, amount, id, date };
}

// This will be replaced by props
const rows = [];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => ({
    el,
    index,
  }));
  stabilizedThis.sort((a, b) => {
    const order = comparator(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilizedThis.map((element) => element.el);
}

const headCells = [
  { id: "customer", alignment: "left", label: "Position" },
  { id: "status", alignment: "left", label: "Name" },
  { id: "id", alignment: "left", label: "Tier" },
  { id: "date", alignment: "left", label: "Website URL" },
];

const EnhancedTableHead = (props) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.alignment}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EnhancedTableToolbar = (props) => {
  const [viewStyle, setViewStyle] = React.useState("List");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const {
    numSelected,
    searchTerm,
    onSearchChange,
    onDownload,
    onEdit,
    onDelete,
    selectedIds,
  } = props;

  const Input = styled(InputBase)`
    color: inherit;
    width: 100%;

    > input {
      color: ${(props) => props.theme.header.search.color};
      padding-top: ${(props) => props.theme.spacing(2.5)};
      padding-right: ${(props) => props.theme.spacing(2.5)};
      padding-bottom: ${(props) => props.theme.spacing(2.5)};
      padding-left: ${(props) => props.theme.spacing(12)};
      width: 160px;
    }
  `;

  const { t } = useTranslation();

  const Search = styled.div`
    border-radius: 2px;
    background-color: ${(props) => props.theme.header.background};
    display: none;
    position: relative;
    width: 100%;

    &:hover {
      background-color: ${(props) =>
        darken(0.05, props.theme.header.background)};
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      display: block;
    }
  `;

  const SearchIconWrapper = styled.div`
    width: 50px;
    height: 100%;
    position: absolute;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 22px;
      height: 22px;
    }
  `;

  const { doChangeProps } = props;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event) => {
    setAnchorEl(null);
  };

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  return (
    <Toolbar>
      <ToolbarTitle>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Grid container>
            {viewStyle === "Card" ? (
              <Grid md={2}>
                <Checkbox />
              </Grid>
            ) : (
              <div></div>
            )}
            <Grid md={10}>
              <Typography>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <Input
                    placeholder={t("Search Sponsors")}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                  />
                </Search>
              </Typography>
            </Grid>
          </Grid>
        )}
      </ToolbarTitle>
      <Spacer />
      <Grid container display="block" textAlign="right">
        {numSelected === 1 ? (
          <Grid>
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                size="large"
                onClick={() => onEdit && onEdit(selectedIds[0])}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                size="large"
                onClick={() => onDelete && onDelete(selectedIds)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        ) : numSelected > 1 ? (
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              size="large"
              onClick={() => onDelete && onDelete(selectedIds)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Paper>
            <Tooltip title="Download List">
              <IconButton
                aria-label="Download List"
                size="large"
                style={{ marginRight: "10px" }}
                onClick={() => onDownload && onDownload()}
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Button
              aria-owns={anchorEl ? "simple-menu" : undefined}
              aria-haspopup="true"
              onClick={handleClick}
              variant="contained"
              color="secondary"
            >
              View
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={(e) => {
                  doChangeProps("List");
                  setViewStyle("List");
                  setAnchorEl(null);
                }}
              >
                List
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  doChangeProps("Card");
                  setViewStyle("Card");
                  setAnchorEl(null);
                }}
              >
                Card
              </MenuItem>
            </Menu>
          </Paper>
        )}
      </Grid>
    </Toolbar>
  );
};

function EnhancedTable({ sponsors = [], onEdit, onDelete, onDownload }) {
  const [viewStyle, setViewStyle] = useState("List");
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("customer");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Convert sponsors data to table format and apply search filter
  const allRows = sponsors.map((sponsor, index) => ({
    id: sponsor.id,
    position: index + 1,
    name: sponsor.name,
    tier: sponsor.tier,
    website: sponsor.website || "",
    logo: sponsor.logo,
  }));

  // Apply search filter
  const rows = allRows.filter((row) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      row.name.toLowerCase().includes(searchLower) ||
      row.tier.toLowerCase().includes(searchLower) ||
      (row.website && row.website.toLowerCase().includes(searchLower))
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      // setViewStyle("Card");
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  // Search handler
  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setPage(0); // Reset to first page when searching
  };

  // Download handler
  const handleDownload = () => {
    // Always use the filtered rows for download
    const csvContent = generateCSV(rows);
    const fileName = searchTerm
      ? `sponsors_filtered_${new Date().toISOString().split("T")[0]}.csv`
      : `sponsors_${new Date().toISOString().split("T")[0]}.csv`;
    downloadCSV(csvContent, fileName);

    // Also call parent's download handler if it exists
    if (onDownload) {
      onDownload(rows, searchTerm);
    }
  };

  // Generate CSV content
  const generateCSV = (data) => {
    const headers = ["Position", "Name", "Tier", "Website"];
    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        [
          row.position,
          `"${row.name}"`,
          `"${row.tier}"`,
          `"${row.website}"`,
        ].join(",")
      ),
    ];
    return csvRows.join("\n");
  };

  // Download CSV file
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Edit handler
  const handleEdit = (sponsorId) => {
    if (onEdit) {
      const sponsor = sponsors.find((s) => s.id === sponsorId);
      onEdit(sponsor);
    }
  };

  // Delete handler
  const handleDelete = (sponsorIds) => {
    if (onDelete) {
      if (
        window.confirm(
          `Are you sure you want to delete ${sponsorIds.length} sponsor(s)?`
        )
      ) {
        onDelete(sponsorIds);
        setSelected([]); // Clear selection after delete
      }
    }
  };

  return (
    <div>
      <Paper>
        <EnhancedTableToolbar
          numSelected={selected.length}
          doChangeProps={setViewStyle}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onDownload={handleDownload}
          onEdit={handleEdit}
          onDelete={handleDelete}
          selectedIds={selected}
        />
      </Paper>
      {/* <ViewMenu doChangeProps={setViewStyle} /> */}
      {viewStyle === "List" ? (
        rows.length > 0 ? (
          <Paper>
            <TableContainer>
              <Table
                aria-labelledby="tableTitle"
                size={"medium"}
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                />
                <TableBody>
                  {stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={`${row.id}-${index}`}
                          selected={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              inputProps={{ "aria-labelledby": labelId }}
                              onClick={(event) => handleClick(event, row.id)}
                            />
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row">
                            <Customer>
                              <Box ml={3}> {row.position} </Box>
                            </Customer>
                          </TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.tier}</TableCell>
                          <TableCell>
                            {row.website ? (
                              <a
                                href={row.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "inherit",
                                  textDecoration: "none",
                                }}
                              >
                                {row.website}
                              </a>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No sponsors added yet. Click "New Sponsor" to add some.
            </Typography>
          </Paper>
        )
      ) : (
        <Box m={1} pt={3} style={{ margin: "0" }}>
          {rows.length > 0 ? (
            <Grid container spacing={6}>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  return (
                    <Grid
                      item
                      xs={12}
                      lg={6}
                      xl={3}
                      key={`card-${row.id}-${index}`}
                    >
                      <Paper>
                        <Card>
                          <div
                            style={{
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                width: "120px",
                                height: "120px",
                                margin: "0px auto 8px",
                                borderRadius: "50%",
                                backgroundColor: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                              }}
                            >
                              {row.logo ? (
                                <img
                                  src={row.logo}
                                  alt={`${row.name} logo`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                <Typography variant="h6" color="text.secondary">
                                  {row.name.substring(0, 2).toUpperCase()}
                                </Typography>
                              )}
                            </div>
                            <Checkbox
                              checked={isItemSelected}
                              value={row.id}
                              color="primary"
                              style={{
                                position: "absolute",
                                top: "0px",
                                left: "0px",
                              }}
                              onChange={(event) => handleClick(event, row.id)}
                            />
                          </div>
                          <CardContent>
                            <Typography
                              gutterBottom
                              variant="h6"
                              component="h2"
                              style={{ textAlign: "center" }}
                            >
                              {row.name}
                            </Typography>
                            <Typography
                              style={{ textAlign: "center" }}
                              color="text.secondary"
                            >
                              {row.tier}
                            </Typography>
                            <Typography
                              style={{
                                textAlign: "center",
                                fontSize: "0.8rem",
                              }}
                              color="text.secondary"
                            >
                              {row.website || "No website"}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Paper>
                    </Grid>
                  );
                })}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No sponsors added yet. Click "New Sponsor" to add some.
              </Typography>
            </Paper>
          )}
        </Box>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}

function SponsorList({ sponsors = [], onEdit, onDelete, onDownload }) {
  return (
    <React.Fragment>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <EnhancedTable
            sponsors={sponsors}
            onEdit={onEdit}
            onDelete={onDelete}
            onDownload={onDownload}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default SponsorList;
